<template>
  <div class="px-12 h-auto">
    <!-- Header -->
    <header class="bg-white border-b border-gray-200 shadow-sm">
      <div class="mx-auto px-4 py-3 flex justify-between items-center">
        <h1 class="text-2xl font-semibold text-blue-900">Doctor Bookings Management</h1>
        <div class="flex items-center">
          <div class="relative mr-3">
            <span
              class="absolute top-0 right-0 -mt-1 -mr-1 bg-gray-700 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
              3
            </span>
            <button class="text-gray-500 hover:text-gray-700">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 01-6 0v-1m6 0H9" />
              </svg>
            </button>
          </div>
          <div class="flex items-center">
            <button @click="dropdownOpen = !dropdownOpen"
              class="relative flex items-center w-18 h-12 overflow-hidden focus:outline-none">
              <img src="@/assets/images/avatar.png" alt="profile" class="w-10 h-10 rounded-full object-cover mr-2" />
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div v-if="dropdownOpen" @click="dropdownOpen = false" class="fixed inset-0 z-10 w-full h-full"></div>
            <transition enter-active-class="transition duration-150 ease-out transform"
              enter-from-class="scale-95 opacity-0" enter-to-class="scale-100 opacity-100"
              leave-active-class="transition duration-150 ease-in transform" leave-from-class="scale-100 opacity-100"
              leave-to-class="scale-95 opacity-0">
              <div v-if="dropdownOpen" class="absolute right-0 z-20 w-48 mt-16 py-2 bg-white rounded-md shadow-xl">
                <button class="block px-4 py-2 text-sm w-full text-gray-700 hover:bg-blue-100 hover:text-orange-400"
                  @click="redirectProfilePage">Profile</button>
                <button class="block px-4 py-2 w-full text-sm text-gray-700 hover:bg-blue-100 hover:text-orange-400"
                  @click="logout">
                  Log out
                </button>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </header>
    <!-- Filter and Search -->
    <div class="flex flex-wrap items-center justify-between py-4">
      <div class="flex items-center space-x-4 flex-wrap">
        <!-- Filters Dropdown -->
        <div v-if="currentTab === 'bookings'" class="relative">
          <button id="dropdownRadioButton"
            class="inline-flex items-center text-gray-500 bg-white border border-gray-300 rounded-md px-4 py-2 text-sm"
            type="button" @click="toggleDropdown">
            <svg class="w-5 h-5 mr-2" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <path stroke="currentColor" stroke-linecap="round" stroke-width="2"
                d="M18.796 4H5.204a1 1 0 0 0-.753 1.659l5.302 6.058a1 1 0 0 1 .247.659v4.874a.5.5 0 0 0 .2.4l3 2.25a.5.5 0 0 0 .8-.4v-7.124a1 1 0 0 1 .247-.659l5.302-6.059c.566-.646.106-1.658-.753-1.658Z" />
            </svg>
            Filters
            <svg class="w-2.5 h-2.5 ml-2" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 10 6">
              <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="m1 1 4 4 4-4" />
            </svg>
          </button>
          <!-- Filter Dropdown -->
          <div v-if="isDropdownOpen"
            class="absolute z-10 mt-2 w-80 p-4 bg-white border border-gray-300 rounded-md shadow-lg">
            <div class="flex">
              <button type="button"
                class="ml-auto -mx-1.5 -my-1.5 p-1.5 text-gray-500 hover:text-gray-700 rounded"
                @click="closeDropdownModal" aria-label="Close">
                <svg class="w-3.5 h-3.5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
                  <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" />
                </svg>
              </button>
            </div>
            <form class="max-w-sm mx-auto" @submit.prevent="applyFilters">
              <div class="mb-4">
                <label for="dateFrom" class="block mb-2 text-sm font-medium text-gray-900">Date From</label>
                <input type="date" v-model="filters.start_date" id="dateFrom"
                  class="px-4 py-2 w-full text-gray-800 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200" />
              </div>
              <div class="mb-4">
                <label for="dateTo" class="block mb-2 text-sm font-medium text-gray-900">Date To</label>
                <input type="date" v-model="filters.end_date" id="dateTo"
                  class="px-4 py-2 w-full text-gray-800 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200" />
              </div>
              <div class="flex justify-end space-x-2">
                <button type="button" @click="cancelFilters"
                  class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-100">
                  Cancel
                </button>
                <button type="submit"
                  class="px-4 py-2 text-sm font-medium text-white bg-orange-400 rounded-md hover:bg-orange-500">
                  Filter
                </button>
              </div>
            </form>
          </div>
        </div>
        <!-- Search Input -->
        <div class="relative flex-1">
          <input type="text" v-model="searchQuery" placeholder="Search facilities or bookings"
            class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200" />
          <button class="absolute left-0 top-0 h-full px-3 text-gray-500">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </button>
        </div>
      </div>
      <!-- Register Facility Button -->
      <div v-if="currentTab === 'facilities'" class="flex items-center space-x-4 mt-4 md:mt-0">
        <button type="button"
          class="mt-2 md:mt-0 flex items-center px-4 py-2 bg-orange-400 text-white rounded-md hover:bg-orange-500 transition"
          @click="openServiceModal">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24"
            stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          Add Service
        </button>
        <button type="button"
          class="mt-2 md:mt-0 flex items-center px-4 py-2 bg-orange-400 text-white rounded-md hover:bg-orange-500 transition"
          @click="openNewBookingFacilityModal">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24"
            stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          Register Facility
        </button>
      </div>
    </div>
    <!-- New Facility Modal Component -->
    <NewBookingFacilityModal 
      :show_facility_modal="showFacilityModal" 
      :facility="selectedFacility"
      @close_facility_modal="closeFacilityModal" 
      @facility-submitted="handleFacilitySubmit"
      @specialists-submitted="handleSpecialistsFinish" />
    <!-- New Service Modal Component -->
    <NewServiceModal :show_service_modal="showServiceModal" @close_service_modal="closeServiceModal" />
    <!-- Tabs -->
    <div class="tabs flex border-b mb-4">
      <button class="px-4 py-2 -mb-px font-semibold text-gray-500 border-b-2"
        :class="{ 'border-orange-400 text-orange-500': currentTab === 'facilities' }"
        @click="currentTab = 'facilities'">
        Facilities
      </button>
      <button class="px-4 py-2 -mb-px font-semibold text-gray-500 border-b-2"
        :class="{ 'border-orange-400 text-orange-500': currentTab === 'bookings' }"
        @click="currentTab = 'bookings'">
        Bookings
      </button>
    </div>
    <component :is="currentTabComponent" ref="tabComponent" :searchQuery="searchQuery" :filters="filters"
      @edit-facility="handleEditFacility" />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import NewBookingFacilityModal from '@/components/forms/NewBookingFacilityModal.vue';
import NewServiceModal from '@/components/forms/NewServiceModal.vue';
import FacilitiesTab from '@/components/tabs/FacilitiesTab.vue';
import BookingsTab from '@/components/tabs/BookingsTab.vue';
import { setApiSystem } from '@/services/api';

const store = useStore();
const route = useRoute();
const router = useRouter();
const toast = useToast();
const dropdownOpen = ref(false);
const searchQuery = ref('');
const currentTab = ref('facilities');
const showFacilityModal = ref(false);
const showServiceModal = ref(false);
const isDropdownOpen = ref(false);
const facilityId = ref(null);
const selectedFacility = ref(null);
const tabComponent = ref(null);

const filters = ref({
  start_date: '',
  end_date: '',
});

onMounted(() => {
  console.log('DoctorBookingsPage mounted');
  setApiSystem('hmis');
  if (!store.getters.isAuthenticated('hmis')) {
    emit('login-required');
  }
});

const currentTabComponent = computed(() => {
  return currentTab.value === 'facilities' ? FacilitiesTab : BookingsTab;
});

const toggleDropdown = () => {
  console.log('Toggling dropdown:', isDropdownOpen.value);
  isDropdownOpen.value = !isDropdownOpen.value;
};

const closeDropdownModal = () => {
  console.log('Closing dropdown');
  isDropdownOpen.value = false;
};

const applyFilters = () => {
  console.log('Filters applied:', filters.value);
  closeDropdownModal();
};

const cancelFilters = () => {
  console.log('Filters cancelled');
  filters.value = { start_date: '', end_date: '' };
  closeDropdownModal();
};

const openNewBookingFacilityModal = () => {
  console.log('Opening new facility modal');
  showFacilityModal.value = true;
  facilityId.value = null;
  selectedFacility.value = null;
};

const openServiceModal = () => {
  console.log('Opening service modal');
  showServiceModal.value = true;
};

const closeFacilityModal = () => {
  console.log('Closing facility modal');
  showFacilityModal.value = false;
  facilityId.value = null;
  selectedFacility.value = null;
};

const closeServiceModal = () => {
  console.log('Closing service modal');
  showServiceModal.value = false;
};

const handleFacilitySubmit = async (responseData) => {
  console.log('Facility submitted:', JSON.stringify(responseData, null, 2));
  if (currentTab.value === 'facilities' && tabComponent.value && typeof tabComponent.value?.fetchFacilities === 'function') {
    try {
      console.log('Calling fetchFacilities on FacilitiesTab');
      await tabComponent.value.fetchFacilities();
      console.log('Facilities fetched after submission');
    } catch (error) {
      console.error('Error fetching facilities after submission:', error, error.response?.data);
    }
  } else {
    console.warn('Cannot fetch facilities:', {
      currentTab: currentTab.value,
      tabComponent: tabComponent.value,
      hasFetchFacilities: typeof tabComponent.value?.fetchFacilities === 'function',
    });
  }
};

const handleSpecialistsFinish = async () => {
  console.log('Specialists submitted');
  if (currentTab.value === 'facilities' && tabComponent.value && typeof tabComponent.value?.fetchFacilities === 'function') {
    try {
      console.log('Calling fetchFacilities after specialists submission');
      await tabComponent.value.fetchFacilities();
      console.log('Facilities fetched after specialists submission');
    } catch (error) {
      console.error('Error fetching facilities after specialists submission:', error, error.response?.data);
    }
  }
  closeFacilityModal();
};

const handleEditFacility = (facility) => {
  console.log('Editing facility:', JSON.stringify(facility, null, 2));
  selectedFacility.value = facility;
  facilityId.value = facility.id;
  showFacilityModal.value = true;
};

const redirectProfilePage = () => {
  console.log('Redirecting to profile page');
  router.push('/profile');
};

const logout = async () => {
  console.log('Logging out');
  
  // Show logout toast message
  toast(
    'You have been logged out successfully. Thank you for using Doctor Bookings!',
    {
      type: 'info',
      timeout: 3000,
      closeOnClick: true,
      pauseOnFocusLoss: true,
      pauseOnHover: true,
      draggable: true,
      draggablePercent: 0.6,
      showCloseButtonOnHover: false,
      hideProgressBar: false,
      closeButton: "button",
      icon: true,
      rtl: false
    }
  );
  
  // Clear auth state first
  store.dispatch('logout');
  
  // Use window.location.href as fallback for more reliable navigation
  try {
    await router.push('/login');
  } catch (error) {
    console.warn('Router navigation failed, using window.location:', error);
    window.location.href = '/login';
  }
};

watch(() => route.path, () => {
  console.log('Route changed, closing dropdown');
  dropdownOpen.value = false;
});
</script>